import type { Action, PermissionSet, Resource, Role } from '@buildbee/contracts';
import { anyMatch, PROJECT_SCOPED_RESOURCES, PROJECT_SCOPED_ROLES, ROLE_DEFAULTS } from './defaults';
import { cellKey, createRoleEvaluator, roleHas, type CellKey, type RoleEvaluator, type RolePolicies } from './hierarchy';
import { CATALOG_CELLS } from './catalog';

export * from './defaults';
export * from './hierarchy';
export * from './catalog';
export * from './policy';

/**
 * Permission engine.
 *
 *  role(R)              = BuildBee defaults for R + ticks inherited from the roles below R
 *                         + the company's role policy for R (see ./hierarchy.ts)
 *  effective(workspace) = role(role) + template.allow - template.deny
 *  effective(project)   = role(projectRole ?? role) + templates + overrides (deny wins)
 *
 * Owners can do everything; billing:manage and workspace:delete are owner-only whatever a template
 * says. `field` and `sub` are project-scoped: for any project-scoped resource they need a project
 * membership, and `ownRecordsOnly` restricts update/delete to records they created or are assigned
 * to. Enforced in the API, never only in the UI.
 */

export type PermissionString = string;

export type ProjectAccess = {
  /** Role on this project (defaults to the workspace role). */
  role?: Role | null;
  /** Permissions of the project member's permission template. */
  template?: PermissionSet | null;
  /** Per-project override set on the project_member row. */
  overrides?: PermissionSet | null;
};

export type PermissionSubject = {
  userId: string;
  role: Role;
  /** Workspace-level permission template attached to the membership. */
  template?: PermissionSet | null;
  /** projectId -> access. Presence of a key means the user is a member of that project. */
  projects?: Readonly<Record<string, ProjectAccess>>;
  /** The company's role policies (`role:<role>` permission templates). Omitted = BuildBee defaults. */
  rolePolicies?: RolePolicies | null;
};

export type ResourceRef =
  | Resource
  | {
      type: Resource;
      /** Project the record belongs to, when it is project-scoped. */
      projectId?: string | null;
      /** created_by / owner of the record (for ownRecordsOnly). */
      ownerId?: string | null;
      /** Users the record is assigned to (for ownRecordsOnly). */
      assigneeIds?: readonly (string | null | undefined)[];
    };

function normalize(ref: ResourceRef) {
  return typeof ref === 'string' ? { type: ref } : ref;
}

/** One evaluator per subject object, so repeated checks reuse the memoized role decisions. */
const evaluators = new WeakMap<object, { policies: RolePolicies | null | undefined; evaluator: RoleEvaluator }>();
function evaluatorFor(subject: PermissionSubject): RoleEvaluator {
  const cached = evaluators.get(subject);
  if (cached && cached.policies === subject.rolePolicies) return cached.evaluator;
  const evaluator = createRoleEvaluator(subject.rolePolicies);
  evaluators.set(subject, { policies: subject.rolePolicies, evaluator });
  return evaluator;
}

type Layers = { role: Role; templates: (PermissionSet | null | undefined)[] };

function layersFor(subject: PermissionSubject, resource: Resource, projectId: string | null | undefined): Layers {
  const projectAccess = projectId ? subject.projects?.[projectId] : undefined;
  if (PROJECT_SCOPED_RESOURCES.has(resource) && projectAccess) {
    return { role: projectAccess.role ?? subject.role, templates: [subject.template, projectAccess.template, projectAccess.overrides] };
  }
  return { role: subject.role, templates: [subject.template] };
}

function ownRecordsOnlyOf(evaluator: RoleEvaluator, layers: Layers): boolean {
  if (layers.role === 'owner') return false;
  let own = evaluator.ownRecordsOnly(layers.role) ?? false;
  for (const t of layers.templates) if (t && t.ownRecordsOnly !== undefined) own = t.ownRecordsOnly;
  return own;
}

/** Explain a decision (useful for debugging and permission UIs). */
export function explain(
  subject: PermissionSubject,
  action: Action,
  resourceRef: ResourceRef,
): { allowed: boolean; reason: string } {
  const ref = normalize(resourceRef);
  const resource = ref.type;

  if (subject.role === 'owner') return { allowed: true, reason: 'owner' };
  if (subject.role === 'client' && resource !== 'portal') {
    return { allowed: false, reason: 'client role is limited to the portal' };
  }

  const projectId = 'projectId' in ref ? ref.projectId : undefined;
  const projectScoped = PROJECT_SCOPED_RESOURCES.has(resource);
  const projectAccess = projectId ? subject.projects?.[projectId] : undefined;

  if (projectScoped && projectId && PROJECT_SCOPED_ROLES.has(subject.role) && !projectAccess) {
    return { allowed: false, reason: 'not a member of this project' };
  }

  const layers = layersFor(subject, resource, projectId);
  if (layers.role === 'owner') return { allowed: true, reason: 'owner on project' };

  const evaluator = evaluatorFor(subject);
  const roleAllows = evaluator.grants(layers.role, resource, action);
  const hardDenied = evaluator.hardDenied(layers.role, resource, action);
  const templateAllows = layers.templates.some((t) => anyMatch(t?.allow, resource, action));
  const templateDenies = layers.templates.some((t) => anyMatch(t?.deny, resource, action));
  const allowed = (roleAllows || templateAllows) && !hardDenied && !templateDenies;

  if (!allowed) return { allowed: false, reason: `${subject.role} lacks ${resource}:${action}` };

  if (ownRecordsOnlyOf(evaluator, layers) && action !== 'view' && action !== 'create') {
    const ownerId = 'ownerId' in ref ? ref.ownerId : undefined;
    const assignees = 'assigneeIds' in ref ? ref.assigneeIds : undefined;
    const own = ownerId === subject.userId || !!assignees?.includes(subject.userId);
    if (!own) return { allowed: false, reason: 'restricted to own or assigned records' };
  }
  return { allowed: true, reason: 'granted' };
}

/** `can(user, action, resource)`: the single permission check used by the API. */
export function can(subject: PermissionSubject, action: Action, resource: ResourceRef): boolean {
  return explain(subject, action, resource).allowed;
}

/** Bind a subject once and get a `can(action, resource)` closure (exposed on tRPC ctx). */
export function createAbility(subject: PermissionSubject) {
  return {
    subject,
    can: (action: Action, resource: ResourceRef) => can(subject, action, resource),
    explain: (action: Action, resource: ResourceRef) => explain(subject, action, resource),
    /**
     * Which projects the subject may list. `'all'` for workspace-wide roles, otherwise the
     * project ids they are a member of. Use it to filter list queries.
     */
    projectScope: (): 'all' | string[] =>
      PROJECT_SCOPED_ROLES.has(subject.role) ? Object.keys(subject.projects ?? {}) : 'all',
    /**
     * Whether the subject only sees and edits records they own or are assigned to (on `projectId`,
     * or workspace-wide). Role policy first, then templates and project overrides; owners never.
     */
    ownRecordsOnly: (projectId?: string | null): boolean => {
      if (subject.role === 'owner') return false;
      const projectAccess = projectId ? subject.projects?.[projectId] : undefined;
      const layers: Layers = projectAccess
        ? { role: projectAccess.role ?? subject.role, templates: [subject.template, projectAccess.template, projectAccess.overrides] }
        : { role: subject.role, templates: [subject.template] };
      return ownRecordsOnlyOf(evaluatorFor(subject), layers);
    },
    /** Workspace-level cells of the Roles catalog this subject has (for hiding controls; the API still checks). */
    cells: (): CellKey[] =>
      CATALOG_CELLS.filter((c) => can(subject, c.action, c.resource)).map((c) => cellKey(c.resource, c.action)),
  };
}
export type Ability = ReturnType<typeof createAbility>;

/** All permission strings a role grants by default, for display in the roles UI. */
export function describeRole(role: Role): PermissionSet {
  return ROLE_DEFAULTS[role];
}

/** Role-level decision with a company's policies (no per-person template). */
export function roleCan(role: Role, action: Action, resource: Resource, policies?: RolePolicies | null): boolean {
  return roleHas(createRoleEvaluator(policies), role, resource, action);
}

/** Roles a given role is allowed to assign to others (no privilege escalation). */
export function assignableRoles(actorRole: Role): Role[] {
  switch (actorRole) {
    case 'owner':
      return ['owner', 'admin', 'sales', 'pm', 'field', 'sub'];
    case 'admin':
      return ['admin', 'sales', 'pm', 'field', 'sub'];
    case 'pm':
      return ['field', 'sub'];
    default:
      return [];
  }
}
